import axios from "axios";
import { initializeConfig } from "../../constants/const";
import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import EnvStore from "../../store/secretStore";
import ENDPOINTS from "../endpoints";
import { apiUrl } from "../../config";
import logger from "../../services/Logger";
import authService from "../../services/AuthService";

export async function getSecret() {
  try {
    const api = apiUrl + ENDPOINTS.GET_SECRETS;
    const response = await axios.get(api);
    return response.data;
  } catch (error) {
    logger.error('Secret', 'Error fetching secret', { error: error.message });
  }
}

export async function initializeConfigFromAPI(callback, errorCallback) {
  const { setSecret } = EnvStore.getState();
  try {
    let configData = localStorage.getItem("firebaseConfig");

    // Invalidate cache if it is missing required fields or has an inconsistent
    // AUTH_WEB_CONFIG (e.g., PROJECT_ID from the data project instead of the IDP
    // project — a known symptom of a pre-IDP-migration cache entry).
    //
    // Consistency rule: AUTHDOMAIN must equal PROJECT_ID + ".firebaseapp.com".
    // If they diverge the frontend will use the wrong Firebase project for
    // email/password sign-in (REST API keyed off PROJECT_ID/apiKey) while Google
    // popup still succeeds (routes through AUTHDOMAIN's Firebase handler).
    if (configData) {
      const parsed = JSON.parse(configData);
      const webCfg = parsed.AUTH_WEB_CONFIG || {};
      const isIdpMode = !!webCfg.API_KEY;

      if (isIdpMode) {
        const projectId = webCfg.PROJECT_ID || "";
        const authDomain = webCfg.AUTHDOMAIN || "";
        const expectedDomain = projectId ? `${projectId}.firebaseapp.com` : "";
        const domainMismatch = projectId && authDomain && authDomain !== expectedDomain;

        if (!parsed.TENANT_ID || !webCfg.PROJECT_ID || !webCfg.API_KEY || domainMismatch) {
          logger.warn('Secret', 'Clearing stale firebaseConfig cache', {
            reason: domainMismatch
              ? `PROJECT_ID/AUTHDOMAIN mismatch (${projectId} vs ${authDomain})`
              : 'missing required AUTH_WEB_CONFIG fields or TENANT_ID',
          });
          localStorage.removeItem("firebaseConfig");
          configData = null;
        }
      }
    }

    if (!configData) {
      const secret = await getSecret();
      if (!secret) {
        throw new Error("Invalid config data");
      }
      // Only cache IDP configs — local/unconfigured setups have no stable Firebase config to cache
      if (secret.AUTH_WEB_CONFIG?.API_KEY) {
        localStorage.setItem("firebaseConfig", JSON.stringify(secret));
      }
      configData = JSON.stringify(secret);
    }

    const config = JSON.parse(configData);

    initializeConfig(config);

    // --- IDP app (quantumzero-public Identity Platform — sign-in only) ---
    const authWebConfig = config["AUTH_WEB_CONFIG"];
    let idpAuth = null;
    if (authWebConfig && authWebConfig.API_KEY) {
      const idpFirebaseConfig = {
        apiKey: authWebConfig["API_KEY"],
        authDomain: authWebConfig["AUTHDOMAIN"],
        projectId: authWebConfig["PROJECT_ID"],
        messagingSenderId: authWebConfig["MESSAGE_ID"],
        appId: authWebConfig["APP_ID"],
      };

      // Reuse the existing "idp" app within this JS session, or create a new one.
      // IMPORTANT: if the existing app was somehow initialised with a different
      // projectId (which should not happen after the cache-validation fix above),
      // we log an error and reload so the correct config is picked up on the next
      // cold-start — Firebase SDK does not support re-initialising a named app.
      const existingIdpApp = getApps().find((a) => a.name === "idp");
      let idpApp;
      if (existingIdpApp) {
        const existingProjectId = existingIdpApp.options?.projectId;
        if (existingProjectId && existingProjectId !== idpFirebaseConfig.projectId) {
          logger.error('Secret', 'IDP Firebase app projectId mismatch — reloading to pick up new config', {
            existing: existingProjectId,
            expected: idpFirebaseConfig.projectId,
          });
          localStorage.removeItem("firebaseConfig");
          window.location.reload();
          return;
        }
        idpApp = existingIdpApp;
      } else {
        idpApp = initializeApp(idpFirebaseConfig, "idp");
      }

      idpAuth = getAuth(idpApp);
      if (config["TENANT_ID"]) {
        idpAuth.tenantId = config["TENANT_ID"];
      }
    }

    // --- SSO providers from Firestore (via /admin/get_secret) ---
    // sso_providers is a top-level array of { id, label, type } on the API response,
    // populated from org Firestore doc. Empty array = local login only (no SSO buttons).
    const ssoProviders = Array.isArray(config?.sso_providers)
      ? config.sso_providers
      : [];

    // Initialise AuthService with the Firebase auth instance and SSO provider list.
    // From this point all auth calls go through authService, not the SDK directly.
    await authService.initialize({ auth: idpAuth, ssoProviders });

    setSecret({
      auth: idpAuth,
      idpAuth,
      callback: callback,
    });
  } catch (error) {
    errorCallback?.();
    logger.error('Secret', 'Error initializing the app', { error: error.message });
  }
}
